/**
 * Vendor Invoices & ITC.
 *
 * Two things Accounts could not get anywhere else: every vendor invoice on
 * file in one list, and the input tax credit accumulated this financial year —
 * company-wide and per Project Manager.
 *
 * WHY THE ITC NUMBER IS NOT JUST A SUM OF GST
 *
 * Section 16 of the CGST Act lets a company claim input tax credit only when
 * it holds a tax invoice, has ACTUALLY RECEIVED the goods, bought from a
 * registered supplier, and PAYS HIM WITHIN 180 DAYS. Miss the last one and the
 * credit has to be reversed with interest.
 *
 * So an invoice sitting on file with nothing matched to it is not credit — it
 * is a claim nobody has evidenced receipt for. And an invoice matched but
 * unpaid for six months is credit about to be taken back. Both are shown
 * separately from the money that is genuinely claimable, because a single
 * "total GST" figure would flatter the position and somebody would file on it.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Ban, CheckCircle2, Clock, FileText, Paperclip, ReceiptIndianRupee, Wallet,
} from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Loading, SearchInput, Select, Stat, Th, sortRows, useSort,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { can } from '@/lib/domain'
import { useQuery, useVendors } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import { useCompanySettings } from '@/lib/settings'

/** 1 April to 31 March. Returns the 1 April that opens the year holding `d`. */
function fyStart(d = new Date()): string {
  const y = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1
  return `${y}-04-01`
}
const fyLabel = (start: string) => {
  const y = Number(start.slice(0, 4))
  return `FY ${y}-${String(y + 1).slice(2)}`
}
/** The current year and the four before it. */
const FY_CHOICES = Array.from({ length: 5 }, (_, i) => {
  const y = Number(fyStart().slice(0, 4)) - i
  return { value: `${y}-04-01`, label: fyLabel(`${y}-04-01`) }
})

const ITC_TONE: Record<string, string> = {
  ELIGIBLE: 'pill-green', AT_RISK: 'pill-amber', REVERSE: 'pill-red',
  UNMATCHED: 'pill-grey', NO_GST: 'pill-grey',
}
const ITC_LABEL: Record<string, string> = {
  ELIGIBLE: 'Claimable',
  AT_RISK: 'Pay within 180 days',
  REVERSE: 'Must be reversed',
  UNMATCHED: 'Not matched yet',
  NO_GST: 'No GST charged',
}

export function VendorInvoices() {
  const { profile } = useAuth()
  const [fy, setFy] = useState(fyStart())
  const [vendor, setVendor] = useState('')
  const [itc, setItc] = useState('')
  const [q, setQ] = useState('')
  const { data: vendors } = useVendors()
  const { sort, toggle } = useSort('invoice_date')
  // 0212: the company's early warning. The 180 days is the law and stays fixed.
  const { itcWarningDays } = useCompanySettings()

  const { data: rows, loading } = useQuery<any>('v_vendor_invoice_register', () => {
    let s = supabase.from('v_vendor_invoice_register').select('*').eq('fy_start', fy)
    if (vendor) s = s.eq('vendor_id', vendor)
    if (itc) s = s.eq('itc_status', itc)
    return s.order('invoice_date', { ascending: false }).limit(2000)
  }, [fy, vendor, itc], { cacheable: false })

  const { data: byPm, loading: pmLoading } = useQuery<any>('v_itc_by_project_manager', () =>
    supabase.from('v_itc_by_project_manager').select('*').eq('fy_start', fy)
      .order('itc_total', { ascending: false }),
    [fy], { cacheable: false })

  // Hooks first, then the guard — the menu hides this screen, but a typed URL
  // must not be a way round it.
  const mayView = can(profile?.role, 'viewItc')
  const mayRecord = can(profile?.role, 'recordInvoice')

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return rows
    return rows.filter((r: any) =>
      [r.invoice_no, r.vendor_name, r.vendor_gstin, r.po_id, r.site_codes, r.project_managers]
        .some((v) => String(v ?? '').toLowerCase().includes(t)))
  }, [rows, q])

  const sorted = useMemo(() => sortRows(filtered, sort, {
    invoice_date: 'date', created_at: 'date', taxable_amount: 'number', gst_amount: 'number',
    total_amount: 'number', matched_amount: 'number', unmatched_amount: 'number',
    outstanding_amount: 'number', itc_net: 'number', days_since_invoice: 'number',
    attachment_count: 'number',
  }), [filtered, sort])

  // Company-wide totals are the sum of the per-manager rows — the same numbers,
  // added once. Two independent calculations would eventually disagree.
  const co = useMemo(() => byPm.reduce((a: any, r: any) => ({
    taxable: a.taxable + Number(r.taxable_value || 0),
    gross: a.gross + Number(r.gst_gross || 0),
    credited: a.credited + Number(r.gst_credited || 0),
    total: a.total + Number(r.itc_total || 0),
    eligible: a.eligible + Number(r.itc_eligible || 0),
    atRisk: a.atRisk + Number(r.itc_at_risk || 0),
    reverse: a.reverse + Number(r.itc_to_reverse || 0),
    cgst: a.cgst + Number(r.itc_cgst || 0),
    sgst: a.sgst + Number(r.itc_sgst || 0),
    igst: a.igst + Number(r.itc_igst || 0),
  }), { taxable: 0, gross: 0, credited: 0, total: 0, eligible: 0, atRisk: 0, reverse: 0,
        cgst: 0, sgst: 0, igst: 0 }), [byPm])

  if (!mayView) {
    return <NotPermitted what="see the vendor invoice and ITC register"
                         who="Accounts, the Head of Stores, the Procurement Manager, the Project Head, the CMD and the CXO" />
  }

  const unmatched = filtered.filter((r: any) => r.itc_status === 'UNMATCHED')
  const unmatchedGst = unmatched.reduce((a: number, r: any) => a + Number(r.gst_amount || 0), 0)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-bold text-slate-800">Vendor Invoices &amp; ITC</h1>
        <span className="pill-grey">{filtered.length}</span>
        <div className="ml-auto w-44">
          <Select value={fy} onChange={(e) => setFy(e.target.value)} options={FY_CHOICES} />
        </div>
      </div>

      {/* ---------------- the ITC position ---------------- */}
      <Card title={`Input tax credit — ${fyLabel(fy)}`}
            subtitle="Company-wide, on material purchases where the goods receipt is on record">
        {pmLoading && <Loading />}
        {!pmLoading && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label="Claimable now" value={rupees(co.eligible)} tone="good"
                    sub="Invoice on file, goods received, paid or within 180 days"
                    icon={<CheckCircle2 className="h-5 w-5" />} />
              <Stat label="Pay within 180 days" value={rupees(co.atRisk)}
                    tone={co.atRisk > 0 ? 'warn' : 'good'}
                    sub={`Unpaid ${itcWarningDays} days or more — credit at risk`}
                    icon={<Clock className="h-5 w-5" />} />
              <Stat label="Must be reversed" value={rupees(co.reverse)}
                    tone={co.reverse > 0 ? 'bad' : 'good'}
                    sub="Unpaid beyond 180 days"
                    icon={<AlertTriangle className="h-5 w-5" />} />
              <Stat label="Total credit booked" value={rupees(co.total)}
                    sub={`On ${rupees(co.taxable)} of taxable purchases`}
                    icon={<ReceiptIndianRupee className="h-5 w-5" />} />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Stat label="CGST" value={rupees(co.cgst)} sub="Within Tripura" />
              <Stat label="SGST" value={rupees(co.sgst)} sub="Within Tripura" />
              <Stat label="IGST" value={rupees(co.igst)} sub="From outside the state" />
              <Stat label="Reduced by credit notes" value={rupees(co.credited)}
                    tone={co.credited > 0 ? 'warn' : 'good'}
                    sub="Tax on material sent back" />
            </div>

            {co.reverse > 0 && (
              <div className="mt-3">
                <Alert tone="error" title={`${rupees(co.reverse)} of credit must be reversed`}>
                  These invoices have gone unpaid for more than <strong>180 days</strong>. Under the
                  second proviso to section 16(2), the credit already taken has to be reversed with
                  interest until the vendor is paid. Filter the list below by
                  <strong> Must be reversed</strong> to see which ones.
                </Alert>
              </div>
            )}
            {co.reverse === 0 && co.atRisk > 0 && (
              <div className="mt-3">
                <Alert tone="warning" title={`${rupees(co.atRisk)} of credit is approaching the 180-day limit`}>
                  Pay these vendors before the limit passes and the credit stays yours. It is a date
                  that goes by quietly — nobody is warned by the tax portal.
                </Alert>
              </div>
            )}

            <p className="mt-3 text-xs text-slate-500">
              Only invoices <strong>matched to a payable</strong> are counted. A payable exists only
              behind an approved goods receipt, so a matched invoice is one where the material
              provably arrived — which is what section 16 requires before any credit may be claimed.
            </p>
          </>
        )}
      </Card>

      {/* ---------------- per project manager ---------------- */}
      <Card title="By Project Manager"
            subtitle="An invoice matched across several sites is split in proportion to the value matched at each">
        {pmLoading && <Loading />}
        {!pmLoading && byPm.length === 0 && (
          <EmptyState icon={<ReceiptIndianRupee className="h-6 w-6" />}
                      title="No credit booked this year"
                      message="No vendor invoice has been matched to a payable in this financial year." />
        )}
        {!pmLoading && byPm.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Project Manager</th>
                  <th className="num">Invoices</th>
                  <th className="num">Sites</th>
                  <th className="num">Taxable value</th>
                  <th className="num">CGST</th>
                  <th className="num">SGST</th>
                  <th className="num">IGST</th>
                  <th className="num">Claimable</th>
                  <th className="num">At risk</th>
                  <th className="num">To reverse</th>
                  <th className="num">Total credit</th>
                </tr>
              </thead>
              <tbody>
                {byPm.map((r: any) => (
                  <tr key={r.project_manager_id ?? r.project_manager}>
                    <td className="text-sm font-medium">{r.project_manager}</td>
                    <td className="num text-xs">{r.invoices}</td>
                    <td className="num text-xs">{r.sites}</td>
                    <td className="num text-xs">{rupees(r.taxable_value)}</td>
                    <td className="num text-xs">{rupees(r.itc_cgst)}</td>
                    <td className="num text-xs">{rupees(r.itc_sgst)}</td>
                    <td className="num text-xs">{rupees(r.itc_igst)}</td>
                    <td className="num text-xs text-emerald-700">{rupees(r.itc_eligible)}</td>
                    <td className={`num text-xs ${Number(r.itc_at_risk) > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                      {rupees(r.itc_at_risk)}
                    </td>
                    <td className={`num text-xs ${Number(r.itc_to_reverse) > 0 ? 'font-semibold text-red-700' : 'text-slate-400'}`}>
                      {rupees(r.itc_to_reverse)}
                    </td>
                    <td className="num text-sm font-bold">{rupees(r.itc_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-semibold">
                  <td>Company total</td>
                  <td /><td />
                  <td className="num text-xs">{rupees(co.taxable)}</td>
                  <td className="num text-xs">{rupees(co.cgst)}</td>
                  <td className="num text-xs">{rupees(co.sgst)}</td>
                  <td className="num text-xs">{rupees(co.igst)}</td>
                  <td className="num text-xs text-emerald-700">{rupees(co.eligible)}</td>
                  <td className="num text-xs text-amber-700">{rupees(co.atRisk)}</td>
                  <td className="num text-xs text-red-700">{rupees(co.reverse)}</td>
                  <td className="num text-sm font-bold">{rupees(co.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* ----------- where invoices are actually entered -----------
          This screen is called "Vendor Invoices", so the first instinct is to
          look for a New invoice button here. There is not one, and there
          should not be: an invoice cannot exist on its own. It has to attach to
          a PAYABLE, and a payable exists only behind an approved goods receipt
          — which is exactly the section 16 evidence the card above describes.
          An invoice with nothing behind it would sit here forever claiming no
          credit. So instead of a button that creates orphans, a signpost. */}
      {mayRecord && (
        <Alert tone="info" title="Entering a vendor invoice">
          <p>
            Invoices are entered against the payable they belong to, not from this
            list — go to <strong>Vendor Payables</strong>, open the payable raised by
            the goods receipt, and press <strong>Enter vendor invoice</strong>.
          </p>
          <p className="mt-2">
            That is deliberate. An invoice must be tied to material that provably
            arrived, or the input tax credit on it cannot be claimed.
          </p>
          {/* 0143. Say where the contractor invoices went, rather than letting
              somebody wonder why a works contractor they were told about is
              absent — the same courtesy the payables and payments screens pay. */}
          <p className="mt-2">
            A <strong>works contractor's</strong> invoice is not entered here at all. It goes
            against the running account bill it belongs to, on{' '}
            <strong>Service Procurement → RA Bills</strong>, and is read there. This list is
            material purchases only.
          </p>
          <Link to="/payables" className="btn-secondary btn-sm mt-3 inline-flex">
            <Wallet className="mr-1 h-4 w-4" /> Go to Vendor Payables
          </Link>
        </Alert>
      )}

      {/* ---------------- the invoices ---------------- */}
      <Card title="All vendor invoices"
            subtitle="Click any row to open the invoice, its matching and its attachments">
        <div className="mb-3 grid gap-3 sm:grid-cols-3">
          <Field label="Vendor">
            <Select value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="All vendors"
                    options={vendors.map((v) => ({ value: v.id, label: v.company_name }))} />
          </Field>
          <Field label="Credit status">
            <Select value={itc} onChange={(e) => setItc(e.target.value)} placeholder="All invoices"
                    options={Object.entries(ITC_LABEL).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Search">
            <SearchInput value={q} onChange={setQ} placeholder="Invoice number, vendor, GSTIN, PO, site…" />
          </Field>
        </div>

        {unmatched.length > 0 && !itc && (
          <div className="mb-3">
            <Alert tone="warning"
                   title={`${unmatched.length} invoice${unmatched.length === 1 ? '' : 's'} not matched to any receipt — ${rupees(unmatchedGst)} of GST not claimable`}>
              The vendor has billed and nobody has yet shown the goods arrived. Until each is matched
              on the payable it belongs to, none of that tax may be claimed. Filter by
              <strong> Not matched yet</strong> to work through them.
            </Alert>
          </div>
        )}

        {loading && <Loading />}
        {!loading && sorted.length === 0 && (
          <EmptyState icon={<FileText className="h-6 w-6" />} title="No invoices"
                      message={`No vendor invoice is dated inside ${fyLabel(fy)}.`} />
        )}
        {!loading && sorted.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="invoice_no" sort={sort} onSort={toggle}>Invoice</Th>
                  <Th id="invoice_date" sort={sort} onSort={toggle}>Date</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Vendor</Th>
                  <Th id="vendor_gstin" sort={sort} onSort={toggle}>GSTIN</Th>
                  <Th id="site_codes" sort={sort} onSort={toggle}>Sites</Th>
                  <Th id="project_managers" sort={sort} onSort={toggle}>Project Manager</Th>
                  <Th id="taxable_amount" sort={sort} onSort={toggle} className="num" align="right">Taxable</Th>
                  <Th id="gst_amount" sort={sort} onSort={toggle} className="num" align="right">GST</Th>
                  <Th id="total_amount" sort={sort} onSort={toggle} className="num" align="right">Invoice</Th>
                  <Th id="itc_net" sort={sort} onSort={toggle} className="num" align="right">ITC</Th>
                  <Th id="days_since_invoice" sort={sort} onSort={toggle} className="num" align="right">Age</Th>
                  <Th id="itc_status" sort={sort} onSort={toggle}>Credit</Th>
                  <Th id="attachment_count" sort={sort} onSort={toggle} className="num" align="right">Files</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r: any) => (
                  <tr key={r.id} className="hover:bg-oval-50">
                    <td>
                      <Link to={`/invoices/${r.id}`}
                            className="font-mono text-xs font-semibold text-oval-700 hover:underline">
                        {r.invoice_no}
                      </Link>
                    </td>
                    <td className="text-xs">{ddmmyyyy(r.invoice_date)}</td>
                    <td className="text-sm">{r.vendor_name}</td>
                    <td className="font-mono text-[11px] text-slate-500">
                      {r.vendor_gstin ?? <span className="pill-grey">unregistered</span>}
                    </td>
                    <td className="text-xs">{r.site_codes}</td>
                    <td className="max-w-[150px] truncate text-xs text-slate-600"
                        title={r.project_managers}>{r.project_managers}</td>
                    <td className="num text-xs">{rupees(r.taxable_amount)}</td>
                    <td className="num text-xs">{rupees(r.gst_amount)}</td>
                    <td className="num text-xs font-medium">{rupees(r.total_amount)}</td>
                    <td className="num text-xs font-semibold text-emerald-700">{rupees(r.itc_net)}</td>
                    <td className={`num text-xs ${Number(r.days_since_invoice) > 180 ? 'font-semibold text-red-700'
                      : Number(r.days_since_invoice) > itcWarningDays ? 'text-amber-700' : 'text-slate-500'}`}>
                      {r.days_since_invoice}d
                    </td>
                    <td>
                      <span className={ITC_TONE[r.itc_status] ?? 'pill-grey'}>
                        {ITC_LABEL[r.itc_status] ?? r.itc_status}
                      </span>
                    </td>
                    <td className="num text-xs">
                      {r.attachment_count > 0 || r.file_path
                        ? <span className="inline-flex items-center gap-1 text-slate-600">
                            <Paperclip className="h-3 w-3" />{r.attachment_count || 1}
                          </span>
                        : <span className="inline-flex items-center gap-1 text-amber-600"
                                title="No copy of the invoice on file">
                            <Ban className="h-3 w-3" />
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Alert tone="info" title="How the four credit states are decided">
        <ul className="mt-1 space-y-1 text-sm">
          <li><strong>Claimable</strong> — invoice on file, matched to a receipt, vendor registered,
            and either paid or still inside 180 days.</li>
          <li><strong>Pay within 180 days</strong> — matched but unpaid for {itcWarningDays} days or more. Still
            claimable; pay the vendor and it stays that way.</li>
          <li><strong>Must be reversed</strong> — unpaid beyond 180 days. Section 16(2) requires the
            credit to be given back with interest until he is paid.</li>
          <li><strong>Not matched yet</strong> — the vendor has billed but no goods receipt has been
            tied to it. Nothing claimable until it is matched on the payable.</li>
          <li><strong>No GST charged</strong> — an unregistered vendor charges no tax, so there is
            no credit to claim. Not a fault.</li>
        </ul>
      </Alert>
    </div>
  )
}
